import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import EmptyState from '@/components/common/EmptyState.jsx';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { MONITOR_REPARTOS, REPARTOS_BY_SUCURSAL } from '@/graphql/repartos.gql.js';
import { useOperatorCompactQaScreen } from '@/utils/operatorCompactQa.js';

const estadoOptions = ['', 'borrador', 'en_curso', 'abierto', 'cerrado', 'rendicion_parcial', 'rendido', 'liquidado'];
const formatCurrency = (value) => `$ ${Number(value || 0).toFixed(2)}`;

export default function RepartosListado() {
  const { sucursal } = useAuth();
  const sucursalId = Number(sucursal?.id || 0);
  const [estado, setEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const variables = useMemo(
    () => ({
      sucursalId,
      estado: estado || null,
      fechaDesde: fechaDesde || null,
      fechaHasta: fechaHasta || null,
    }),
    [sucursalId, estado, fechaDesde, fechaHasta],
  );

  const [listQ] = useQuery({
    query: REPARTOS_BY_SUCURSAL,
    variables,
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });
  const [monitorQ] = useQuery({
    query: MONITOR_REPARTOS,
    variables: { sucursalId },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });

  const items = listQ.data?.repartosBySucursal || [];
  const queryError = listQ.error?.message || monitorQ.error?.message || '';
  const kpiCards = useMemo(() => {
    const kpi = monitorQ.data?.monitorRepartos || {
      abiertos: 0,
      en_rendicion: 0,
      liquidados: 0,
      total_cr_pendiente: 0,
    };
    return [
      ['Abiertos', kpi.abiertos],
      ['En rendicion', kpi.en_rendicion],
      ['Liquidados', kpi.liquidados],
      ['CR pendiente', formatCurrency(kpi.total_cr_pendiente)],
    ];
  }, [monitorQ.data]);
  const resultsMaxHeight = 'calc(100vh - 250px)';

  useOperatorCompactQaScreen('Repartos', [
    'KPIs resumidos en chips',
    'Tabla con scroll interno',
  ]);

  return (
    <div
      className="mx-auto w-full max-w-[1400px] space-y-1.5 px-4 py-1.5"
      data-qa-screen="repartos"
    >
      <PageHeaderCompact
        title="Repartos"
        description="Monitor operativo por sucursal con acciones visibles y filtros compactos."
        action={
          <div className="flex items-center gap-2">
            <Link
              to="/repartos/estadisticas"
              className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Estadísticas
            </Link>
            <Link
              to="/cargas/repartos/nuevo"
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Nuevo reparto
            </Link>
          </div>
        }
      />

      <section className="flex flex-wrap items-center gap-1.5 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-2.5 py-1.5 shadow-sm">
        {kpiCards.map(([label, value]) => (
          <article key={label} className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.55)] px-2.5 py-1">
            <p className="text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]">
              {label}
            </p>
            <p className="text-sm font-semibold text-[rgb(var(--fg))]">{value}</p>
          </article>
        ))}
      </section>

      <section
        className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-2.5"
        data-qa-filters="true"
      >
        <div className="grid grid-cols-12 gap-2.5">
          <label className="col-span-12 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--muted))] md:col-span-3">
            Desde
            <input
              type="date"
              value={fechaDesde}
              onChange={(event) => setFechaDesde(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </label>
          <label className="col-span-12 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--muted))] md:col-span-3">
            Hasta
            <input
              type="date"
              value={fechaHasta}
              onChange={(event) => setFechaHasta(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </label>
          <label className="col-span-12 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--muted))] md:col-span-3">
            Estado
            <select
              value={estado}
              onChange={(event) => setEstado(event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              {estadoOptions.map((option) => (
                <option value={option} key={option}>
                  {option || 'Todos los estados'}
                </option>
              ))}
            </select>
          </label>
          <div className="col-span-12 flex items-end justify-end md:col-span-3">
            <button
              type="button"
              onClick={() => {
                setEstado('');
                setFechaDesde('');
                setFechaHasta('');
              }}
              className="w-full rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm font-semibold text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.12)] md:w-auto"
            >
              Limpiar
            </button>
          </div>
        </div>

        {queryError ? (
          <div className="mt-3 rounded-2xl border border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
            {queryError}
          </div>
        ) : null}

        <div
          className="mt-2 overflow-auto rounded-2xl border border-[rgb(var(--border))]"
          style={{ maxHeight: resultsMaxHeight }}
          data-qa-results-scroll="true"
        >
          <table className="min-w-full text-sm">
            <thead
              className="sticky top-0 bg-[rgb(var(--subpanel))] text-left text-[11px] uppercase tracking-[0.12em] text-[rgb(var(--muted))]"
              data-qa-results-header="true"
            >
              <tr>
                <th className="px-3 py-1.5">Reparto</th>
                <th className="px-3 py-1.5">Estado</th>
                <th className="px-3 py-1.5">Fecha</th>
                <th className="px-3 py-1.5">Guias</th>
                <th className="px-3 py-1.5">Flete</th>
                <th className="px-3 py-1.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {listQ.fetching ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-[rgb(var(--muted))]">
                    Cargando repartos...
                  </td>
                </tr>
              ) : null}
              {items.map((reparto) => (
                <tr key={reparto.id} className="border-t border-[rgb(var(--border))]" data-qa-row="true">
                  <td className="px-3 py-1.5 font-medium">#{reparto.id}</td>
                  <td className="px-3 py-1.5">
                    <span className="rounded-full border border-[rgb(var(--border))] px-2 py-0.5 text-[11px]">
                      {reparto.estado}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">{reparto.fecha}</td>
                  <td className="px-3 py-1.5">{reparto.total_guias}</td>
                  <td className="px-3 py-1.5">{formatCurrency(reparto.total_flete)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Link
                      className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                      to={`/cargas/repartos/${reparto.id}`}
                    >
                      Ver
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!listQ.fetching && items.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="Sin repartos"
                description="No hay repartos con los filtros activos. Crea uno nuevo o amplia el rango de fechas."
              />
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
